/**
 * TQC2060_R01 시험성적 등록 - 시험 상세(시료·시험결과) AG Grid 테이블
 * 헤더의 시료수(sampleCount)에 맞춰 행 수를 자동 동기화하고, 시료명은 차종·품명 등으로 자동완성한다.
 */
import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction,  postSortHandler, useCellChangeHandler,  createCellCssClassRules, GridCellConfig } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {  defaultColDef, getDefaultRowIndexColumnConfig,  } from "src/utils/grid/gridStyleUtils";
import { useTQC2060_R01Store } from "./Store_TQC2060_R01";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultTQC2060_R01DtlDto, TQC2060_R01DetailDto } from "./Dto_TQC2060_R01";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useCodeSearch } from "src/hooks/useCodeSearch";

/** 시료(sampleInfo) 셀 커스텀 렌더러 — autoCompletedSampleInfo 우선 표시 */
const SampleInfoRenderer = (props: any) => {
    const { value, data } = props;

    const displayValue = data?.autoCompletedSampleInfo || value || '';

    return (
        <div className="w-full h-full flex items-center px-2">
            <span>{displayValue}</span>
        </div>
    );
};

/** 시료 자동완성 조합 규칙 — enabled일 때 parts를 공백으로 이어 붙인다 */
type SampleOptionRule = {
	enabled: boolean;
	parts: string[];
};

/** 삭제 목록(deletedList)에 없는 활성 상세 행만 반환 */
const getActiveDetails = (
	detailList: TQC2060_R01DetailDto[],
	deletedList: string[]
	): TQC2060_R01DetailDto[] => {
	const deletedSet = new Set(deletedList.map(String));

	return detailList.filter(detail => !deletedSet.has(String(detail.valueIndex)));
};

/** 상세 목록에서 가장 큰 valueIndex — 신규 행 ID 부여 시 사용 */
const getMaxValueIndex = (detailList: TQC2060_R01DetailDto[]): number => {
	if (detailList.length === 0) {
		return 0;
	}

	return Math.max(...detailList.map(detail => detail.valueIndex || 0));
};

/** 시료수 증가 시 추가할 빈 시료 행 count건 생성 (inserted=true, 시료명 자동완성 값 포함) */
const createSampleRows = (
	count: number,
	maxValueIndex: number,
	activeCount: number,
	sampleInfo: string
	): TQC2060_R01DetailDto[] => {
	return Array.from({ length: count }, (_, index) => ({
		...defaultTQC2060_R01DtlDto,
		valueIndex: maxValueIndex + index + 1,
		orderIndex: activeCount + index + 1,
		orderIndexSeq: 0,
		inserted: true,
		isModified: false,
		autoCompletedSampleInfo: sampleInfo,
		sampleInfo,
	}));
};

/** 시료수 동기화 시 기존 입력값을 유지해야 하는 행인지 판별 (기존행·수정됨·시험결과 입력됨) */
const shouldPreserveSampleRow = (row: TQC2060_R01DetailDto): boolean => {
	return Boolean(
		!row.inserted ||
		row.isModified ||
		row.testValue?.trim()
	);
};

/** 시료수 변경 시 기존 행의 시료/시험결과를 보존하거나, 신규 행에 자동완성 시료명을 일괄 적용 */
const normalizeSampleRows = (
	rows: TQC2060_R01DetailDto[],
	sampleInfo: string
	): TQC2060_R01DetailDto[] => {
	return rows.map(row => {
		if (shouldPreserveSampleRow(row)) {
		return {
			...row,
			sampleInfo: row.sampleInfo || row.autoCompletedSampleInfo || "",
			autoCompletedSampleInfo: row.autoCompletedSampleInfo || row.sampleInfo || "",
			testValue: row.testValue || "",
		};
		}

		return {
		...row,
		sampleInfo,
		autoCompletedSampleInfo: sampleInfo,
		testValue: "",
		};
	});
};

/**
 * 헤더 정보(차종·품명·품번)로 시료 자동완성 후보 문자열 목록 생성
 * 우선순위: 차종+차종명+품명 → 차종+차종명+품번 → … → 단일 필드
 */
const buildSampleOptions = (
	carModel: string,
	carModelName: string,
	itemNm: string,
	itemNo: string
	): string[] => {
	const rules: SampleOptionRule[] = [
		{
		enabled: Boolean(carModel && carModelName && itemNm),
		parts: [carModel, carModelName, itemNm],
		},
		{
		enabled: Boolean(carModel && carModelName && itemNo),
		parts: [carModel, carModelName, itemNo],
		},
		{
		enabled: Boolean(carModel && !carModelName && itemNm),
		parts: [carModel, itemNm],
		},
		{
		enabled: Boolean(carModel && !carModelName && itemNo),
		parts: [carModel, itemNo],
		},
		{
		enabled: Boolean(carModelName && !carModel && itemNm),
		parts: [carModelName, itemNm],
		},
		{
		enabled: Boolean(carModelName && !carModel && itemNo),
		parts: [carModelName, itemNo],
		},
		{
		enabled: Boolean(carModel),
		parts: [carModel],
		},
		{
		enabled: Boolean(carModelName),
		parts: [carModelName],
		},
		{
		enabled: Boolean(itemNm),
		parts: [itemNm],
		},
		{
		enabled: Boolean(itemNo),
		parts: [itemNo],
		},
	];

	return rules
		.filter(rule => rule.enabled)
		.map(rule => rule.parts.join(" "));
	};

/** localStorage 컬럼 너비·순서 저장 키 */
const COLUMN_STATE_KEY = 'TQC2060_R01-detail-grid-column-state';

/** 클릭 시 편집 모드로 진입할 수 있는 컬럼 ID 집합 */
const commonInputColumns = new Set(['sampleInfo', 'testValue', 'remark']);

/** 셀 비활성·포인터·정렬 등 그리드 CSS 클래스 규칙 설정 */
interface Props {
	/** 부모(Content)에서 전달하는 AG Grid ref — 저장·포커스 제어용 */
	detailGridRef: React.RefObject<AgGridReact<TQC2060_R01DetailDto> | null>;
}

/** 컬럼 상태 복원 시 사용할 헤더 표시명 매핑 */
const headerNames: Record<string, string> = {
		rowIndex: "",
		sampleInfo: '시료',
		testValue: '시험결과',
}

export const DetailTable_TQC2060_R01 = (props: Props) => {

	const { detailGridRef } = props;

	const {
		TQC2060_R01DetailList, setTQC2060_R01DetailList,
		updateTQC2060_R01DetailList,
		selectedTQC2060_R01DetailList, setSelectedTQC2060_R01DetailList,
		initSelectedTQC2060_R01DetailList,
		deletedTQC2060_R01DetailList, addDeletedTQC2060_R01DetailList,
		originalTQC2060_R01DetailList,
		editTQC2060_R01,
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
		setPrevSampleCount,
		setIsEditing,
	} = useTQC2060_R01Store();
	const { carModelList } = useCommonCodeStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef: detailGridRef,
		key: 'valueIndex',
		gridData: TQC2060_R01DetailList,
		defaultGridData: defaultTQC2060_R01DtlDto,
		selectedRow: selectedTQC2060_R01DetailList,
		originalGridData: originalTQC2060_R01DetailList,
		updateRow: (index: number, data: any) => updateTQC2060_R01DetailList(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['sampleInfo'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],

		pointerCells: ['testValue'],
		insertedPointerCells: [''],
		commonPointerCells: [''],

		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	/** 선택·삭제·비활성 상태에 따른 셀 CSS 클래스가 적용된 defaultColDef */
	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<TQC2060_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedTQC2060_R01DetailList,
            deletedList: deletedTQC2060_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [createCellClassRules, selectedTQC2060_R01DetailList, deletedTQC2060_R01DetailList, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns]);

	/** 키보드·품목검색 훅에서 편집 허용할 컬럼 목록 */
	const defaultEditableColumns = ['sampleInfo', 'testValue', 'remark'];

	/** editTQC2060_R01 헤더 + 차종 공통코드로 시료 자동완성 옵션 배열 반환 */
	const getSampleOptions = useCallback((): string[] => {
	if (!editTQC2060_R01) {
		return [];
	}

	const carModel = editTQC2060_R01.carModel || "";
	const itemNm = editTQC2060_R01.itemNm || "";
	const itemNo = editTQC2060_R01.itemNo || "";

	const carModelInfo = carModelList.find(item => item.minorCd === carModel);
	const carModelName = carModelInfo?.codeNm || carModel;

	return buildSampleOptions(carModel, carModelName, itemNm, itemNo);
	}, [editTQC2060_R01, carModelList]);

	/** 기본 컬럼 정의 — 시료(자동완성 클릭), 시험결과(숫자 파싱·붙여넣기 허용) */
	const baseColumnDefs: ColDef<TQC2060_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '시료', colId: 'sampleInfo', field: 'sampleInfo', width: 200,
			editable: false,
			cellRenderer: SampleInfoRenderer,
			onCellClicked: (params: any) => {
				if (params.node?.data) {
					const sampleOptions = getSampleOptions();
					if (sampleOptions.length > 0) {
						const autoCompletedValue = sampleOptions[0];
						params.node.data.autoCompletedSampleInfo = autoCompletedValue;
						params.node.data.sampleInfo = autoCompletedValue;
						params.api.refreshCells({ rowNodes: [params.node], columns: ['sampleInfo'] });
					}
				}
			},
		},
		{
			headerName: '시험결과', colId: 'testValue', field: 'testValue', width: 200,
			editable: true,
			pasteable: true,
			valueParser: (params) => Number(String(params.newValue ?? '').replaceAll(",", "").trim()) || 0,
		},

	], [getSampleOptions]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

/** 사용자 컬럼 상태(detailColumnState)가 있으면 복원, 없으면 baseColumnDefs 사용 */
	const columnDefs: ColDef<TQC2060_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}

		return GridCustomColumnDefs<TQC2060_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	/** 셀 값 변경 시 isEditing 플래그로 sampleCount 동기화 지연 후 스토어 갱신 */
	const handleCellChange = useCellChangeHandler({
		dataList: TQC2060_R01DetailList,
		originalDataList: originalTQC2060_R01DetailList,
		updateRow: (index: number, data: TQC2060_R01DetailDto) => {
			setIsEditing(true);

			updateTQC2060_R01DetailList(index, data);

			setTimeout(() => {
				setIsEditing(false);
			}, 100);
		},
		customBeforeUpdate: (updatedData) => {
			return updatedData;
		},
	});

	/** 행 클릭 시 단일 선택 토글 (하단 합계 행 제외) */
	const handleRowClick = useCallback((e: RowClickedEvent<TQC2060_R01DetailDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedTQC2060_R01DetailList.includes(rowData['valueIndex'] as any);

		if (isSelected) {
			setSelectedTQC2060_R01DetailList([]);
		} else {
			setSelectedTQC2060_R01DetailList([rowData['valueIndex']] as any);
		}
	}, [selectedTQC2060_R01DetailList, setSelectedTQC2060_R01DetailList]);

	/** 입력 컬럼 클릭 시 즉시 편집 모드 진입 */
    const cellClickActions: CustomCellClickAction<TQC2060_R01DetailDto>[] = useMemo(() => [
        {
            validate: (field) => {
                return commonInputColumns.has(field);
			},
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);

	const handleCellClick = CreateCellClickHandler(TQC2060_R01DetailList, 'valueIndex', cellClickActions);

	const { searchExactCode } = useCodeSearch({});

	/** 품목 검색 모달 연동용 빈 핸들러 (현재 모달 미사용) */
	const detailTableHandlers = {
		settingCodeSearchModalProps: () => {
		}
	};

	/** 키보드 화살표·품목코드 검색·수량 필드(testValue) 이동 등 셀 키다운 처리 */
	const cellEventHandlers = useDetailTableWithItemSearch<TQC2060_R01DetailDto>({
		detailList: TQC2060_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목',
			responseMapping: {
				itemCd: 'sampleInfo',
				itemNm: 'testValue',
			},
			clearFields: {
				sampleInfo: "", testValue: "", remark: ""
			},
				searchFunction: (code: string, onSuccess?: (item: any) => void) =>
					searchExactCode('item', code, onSuccess)
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps,
		},
		keyDownConfig: {
			qtyField: 'testValue',
			enableArrowNavigation: true
		}
	});

	/** 시료·시험결과 더블클릭 시 편집 모드 진입 */
	const cellDoubleClickActions: CustomCellDoubleClickAction<TQC2060_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field) => field === 'sampleInfo' || field === 'testValue',
			action: (e) => {
				e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
			}
		},
	], []);

	const handleCellDoubleClick = CreateCellDoubleClickHandler(TQC2060_R01DetailList, 'valueIndex', cellDoubleClickActions);

	/** 그리드 정렬 후 orderIndex 등 스토어 목록 재정렬 */
	const handlePostSort = useCallback((params: PostSortRowsParams<TQC2060_R01DetailDto>) => {
		postSortHandler(params, TQC2060_R01DetailList, originalTQC2060_R01DetailList, setTQC2060_R01DetailList);
	}, [TQC2060_R01DetailList, originalTQC2060_R01DetailList, setTQC2060_R01DetailList]);

	/** 하단 고정 행 — 현재 상세 건수 표시 */
	const pinnedBottomRowData = useMemo(() => {

		return [{
			sampleInfo: `${TQC2060_R01DetailList.length.toLocaleString()}건`,
		}];
	}, [TQC2060_R01DetailList]);

	/**
	 * 헤더 시료수(targetCount)에 맞춰 상세 행 수를 맞춤
	 * - 부족: 신규 행 추가 + 시료 자동완성
	 * - 초과: inserted 행은 물리 삭제, 기존 행은 deletedList에 추가
	 */
	const syncDetailRowsBySampleCount = useCallback((targetCount: number) => {
		if (targetCount <= 0) {
			setTQC2060_R01DetailList([]);
			return;
		}

		/** 삭제 표시되지 않은 현재 활성 상세 행 */
		const activeDetails = getActiveDetails(
			TQC2060_R01DetailList,
			deletedTQC2060_R01DetailList
		);

		/** 활성 행 수 — targetCount와 비교해 추가/삭제 판단 */
		const currentActiveCount = activeDetails.length;

		if (currentActiveCount === targetCount) {
			return;
		}

		if (currentActiveCount < targetCount) {
			const sampleOptions = getSampleOptions();
			/** 첫 번째 자동완성 시료명 — 신규 행에 기본값으로 적용 */
			const autoCompletedValue = sampleOptions[0] || "";
			/** 추가해야 할 행 수 */
			const needToAdd = targetCount - currentActiveCount;
			/** 신규 행 valueIndex 부여 기준 */
			const maxValueIndex = getMaxValueIndex(TQC2060_R01DetailList);

			/** 기존 행 시료명 정규화 후 유지 */
			const preservedRows = normalizeSampleRows(activeDetails, autoCompletedValue);
			/** 부족분만큼 신규 시료 행 생성 */
			const newRows = createSampleRows(
			needToAdd,
			maxValueIndex,
			activeDetails.length,
			autoCompletedValue
			);

			setTQC2060_R01DetailList([...preservedRows, ...newRows]);
			return;
		}

		/** 줄여야 할 행 수 */
		const needToRemove = currentActiveCount - targetCount;
		/** 맨 아래 행부터 제거 대상 선정 */
		const rowsToRemove = activeDetails.slice(-needToRemove);
		/** 아직 저장 전(inserted) 행 — deletedList 없이 목록에서 바로 제거 */
		const insertedRowIds = rowsToRemove
			.filter(row => row.inserted)
			.map(row => String(row.valueIndex));

		if (insertedRowIds.length > 0) {
			/** inserted 행만 제외한 나머지 활성 행 */
			const remainingRows = activeDetails.filter(
			row => !insertedRowIds.includes(String(row.valueIndex))
			);

			setTQC2060_R01DetailList(remainingRows);
			return;
		}

		addDeletedTQC2060_R01DetailList(
			rowsToRemove.map(row => String(row.valueIndex))
		);
	}, [
		TQC2060_R01DetailList,
		deletedTQC2060_R01DetailList,
		setTQC2060_R01DetailList,
		addDeletedTQC2060_R01DetailList,
		getSampleOptions,
	]);

	/**
	 * editTQC2060_R01.sampleCount 변경 감지 → 상세 행 수 동기화
	 * isEditing 중이거나 prevSampleCount와 동일하면 스킵 (입력 중 행 삭제 방지)
	 */
	useEffect(() => {
		/** 헤더에 입력된 시료수 (문자열·숫자 혼용 가능) */
		const sampleCount = editTQC2060_R01?.sampleCount;

		if (sampleCount == null) {
			return;
		}

		/** 동기화 목표 행 수 */
		const targetCount = Number(sampleCount);
		/** isEditing·prevSampleCount 등 동기화 제어용 스토어 스냅샷 */
		const storeState = useTQC2060_R01Store.getState();
		/** testResultNo 없음 = 신규 등록 (prevSampleCount 초기화 분기용) */
		const isNewRecord = !editTQC2060_R01?.testResultNo;

		if (isNewRecord && storeState.prevSampleCount !== undefined) {
			setPrevSampleCount(undefined);
		}

		if (storeState.prevSampleCount === targetCount) {
			return;
		}

		if (storeState.isEditing) {
			setPrevSampleCount(targetCount);
			return;
		}

		syncDetailRowsBySampleCount(targetCount);

		if (useTQC2060_R01Store.getState().prevSampleCount !== targetCount) {
			setPrevSampleCount(targetCount);
		}
	}, [
		editTQC2060_R01?.sampleCount,
		editTQC2060_R01?.testResultNo,
		setPrevSampleCount,
		syncDetailRowsBySampleCount,
	]);

	return (
		<div className="w-full h-full">

			<GridContextWrapper<TQC2060_R01DetailDto>
				enableCellCalculation
				gridWrapperRef={gridWrapperRef}
				gridRef={detailGridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setTQC2060_R01DetailList}
				initSelected={initSelectedTQC2060_R01DetailList}
				defaultRowData={defaultTQC2060_R01DtlDto}
				baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}
				setColumnState={setDetailColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<TQC2060_R01DetailDto>
					ref={detailGridRef}
					rowData={paddedGridData}
					getRowId={getRowId}
					columnDefs={columnDefs}
					defaultColDef={cellCssDefaultColDef}

					rowClassRules={createRowClassRules<TQC2060_R01DetailDto>({
						idKey: 'valueIndex',
						selectedList: selectedTQC2060_R01DetailList,
						deletedList: deletedTQC2060_R01DetailList
					})}
					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					pinnedBottomRowData={pinnedBottomRowData}
					onPostSortRows={handlePostSort}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setDetailColumnState}
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={detailScrollTop}
					setScrollTop={setDetailScrollTop}
				/>
			</GridContextWrapper>
		</div>
	)
}

export default DetailTable_TQC2060_R01;
